import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiList } from '../api';

function formatMoney(value) {
  const num = Number(value || 0);
  return `${num.toLocaleString('ar-LY', { maximumFractionDigits: 2 })} د.ل`;
}

function StoreDebtTransfer() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [fromStoreId, setFromStoreId] = useState('');
  const [toStoreId, setToStoreId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch('/stores');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'تعذر تحميل المحلات');
      const list = getApiList(data, 'stores').filter(store => !store.deleted_at && String(store.status || 'active') !== 'disabled');
      setStores(list);
    } catch (err) {
      setError(err.message || 'تعذر تحميل المحلات');
    } finally {
      setLoading(false);
    }
  };

  const fromStore = useMemo(() => stores.find(store => String(store.id) === String(fromStoreId)), [stores, fromStoreId]);
  const toStore = useMemo(() => stores.find(store => String(store.id) === String(toStoreId)), [stores, toStoreId]);
  const amountNumber = Number(amount || 0);

  const submit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!fromStoreId) return setError('اختر المحل الذي سيتم تخفيض دينه');
    if (!toStoreId) return setError('اختر المحل الذي سيتحمل الدين');
    if (fromStoreId === toStoreId) return setError('لا يمكن نقل الدين لنفس المحل');
    if (!amountNumber || amountNumber <= 0) return setError('اكتب مبلغ صحيح أكبر من صفر');
    if (fromStore && Number(fromStore.balance || 0) < amountNumber) return setError(`المبلغ أكبر من دين ${fromStore.name}`);

    try {
      setSaving(true);
      const response = await apiFetch('/stores/debt-transfer', {
        method: 'POST',
        body: JSON.stringify({
          from_store_id: fromStoreId,
          to_store_id: toStoreId,
          amount: amountNumber,
          reason
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'تعذر نقل الدين');
      setMessage(data.message || 'تم نقل الدين بين المحلات بنجاح');
      setAmount('');
      setReason('');
      await loadStores();
    } catch (err) {
      setError(err.message || 'تعذر نقل الدين');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="store-debt-transfer-page" dir="rtl">
      <div className="debt-transfer-hero">
        <div>
          <p className="debt-transfer-kicker">عملية مباشرة بدون محفظة</p>
          <h1>نقل دين بين المحلات</h1>
          <p>استخدمها عندما يتحمل محل دين محل آخر. هذه العملية لا تغيّر المحافظ ولا رصيد المستخدم.</p>
        </div>
        <button type="button" onClick={() => navigate('/transactions')} className="debt-transfer-secondary-btn">
          عرض العمليات
        </button>
      </div>

      {error && <div className="debt-transfer-alert error">{error}</div>}
      {message && <div className="debt-transfer-alert success">{message}</div>}

      <div className="debt-transfer-layout">
        <form onSubmit={submit} className="debt-transfer-card">
          <label>
            <span>من المحل</span>
            <select value={fromStoreId} onChange={(e) => setFromStoreId(e.target.value)} disabled={loading || saving}>
              <option value="">اختر المحل الذي سينقص دينه</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name} — دينه {formatMoney(store.balance)}</option>
              ))}
            </select>
          </label>

          <label>
            <span>إلى المحل</span>
            <select value={toStoreId} onChange={(e) => setToStoreId(e.target.value)} disabled={loading || saving}>
              <option value="">اختر المحل الذي سيزيد دينه</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name} — دينه {formatMoney(store.balance)}</option>
              ))}
            </select>
          </label>

          <label>
            <span>المبلغ</span>
            <input type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="مثال: 500" disabled={saving} />
          </label>

          <label>
            <span>السبب / الملاحظة</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="مثال: محل آخر تحمل دينه" disabled={saving} rows={3} />
          </label>

          <button type="submit" className="debt-transfer-main-btn" disabled={saving || loading}>
            {saving ? 'جاري التنفيذ...' : 'تنفيذ نقل الدين مباشرة'}
          </button>
        </form>

        <div className="debt-transfer-preview">
          <h2>معاينة التأثير</h2>
          <div className="preview-row">
            <span>المحل الذي ينقص دينه</span>
            <strong>{fromStore ? fromStore.name : 'لم يتم الاختيار'}</strong>
            {fromStore && <small>الرصيد الحالي: {formatMoney(fromStore.balance)}</small>}
            {fromStore && amountNumber > 0 && <b>بعد النقل: {formatMoney(Number(fromStore.balance || 0) - amountNumber)}</b>}
          </div>
          <div className="preview-arrow">⇩</div>
          <div className="preview-row orange">
            <span>المحل الذي يزيد دينه</span>
            <strong>{toStore ? toStore.name : 'لم يتم الاختيار'}</strong>
            {toStore && <small>الرصيد الحالي: {formatMoney(toStore.balance)}</small>}
            {toStore && amountNumber > 0 && <b>بعد النقل: {formatMoney(Number(toStore.balance || 0) + amountNumber)}</b>}
          </div>
          <p className="debt-transfer-note">
            لا يتم خصم أو إضافة أي مبلغ في المحافظ. ستظهر العملية في سجل العمليات كـ نقل دين صادر ووارد.
          </p>
        </div>
      </div>
    </div>
  );
}

export default StoreDebtTransfer;
